import { Injectable, computed, inject, signal } from '@angular/core';
import { injectQuery, injectQueryClient, queryOptions } from '@ngneat/query';
import { derivedAsync } from 'ngxtension/derived-async';
import { TenantUrlUtilService } from '@shared/services/tenant-url-util.service';
import {
  OrderDetailPayload,
  OrderGroupDetailPayload,
} from '../models/order-detail.model';
import { OrderRexAriaPayload } from '../models/order-rex-aria.model';
import { OrderGroupPayload } from '../models/order.model';
import { OrderDetailService } from './order-detail.service';
import {
  OrderInvoicingStatusPayload,
  OrderInvoicingStatusService,
} from './order-invoicing-status.service';
import { OrderProductService } from './order-product.service';
import { OrderRexAriaService } from './order-rex-aria.service';
import {
  OrderTimelinePayload,
  OrderTimelineService,
} from './order-timeline.service';
import { OrderService } from './order.service';
import { SimpleLogger, LOG_MODULES } from '@core/services/logging';

@Injectable({ providedIn: 'root' })
export class OrdersGlobalState {
  private readonly query = injectQuery();
  private readonly queryClient = injectQueryClient();
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly orderService = inject(OrderService);
  private readonly orderDetailService = inject(OrderDetailService);
  private readonly orderProductService = inject(OrderProductService);
  private readonly orderTimelineService = inject(OrderTimelineService);
  private readonly orderInvoicingStatusService = inject(
    OrderInvoicingStatusService
  );
  private readonly orderRexAriaService = inject(OrderRexAriaService);
  private readonly logger = inject(SimpleLogger);

  readonly statusFilter = signal<
    'IN_PROGRESS' | 'DELIVERED' | 'CANCELED' | null
  >(null);

  readonly orderTypeFilter = signal<string | null>(null);

  readonly orderPayload = computed(() => {
    const tenantInfo = this.tenantUrlUtil.tenantUrlInfo();
    const dateRangeForAPI = this.tenantUrlUtil.dateRangeForAPI();

    const tenantId = tenantInfo.tenant;
    const documentType = tenantInfo.documentType;
    const documentValue = tenantInfo.documentValue;


    
    if (!tenantId || !documentValue || !dateRangeForAPI) {
      return null;
    }

    if (documentType === 'EMAIL') {
      return {
        tenantId,
        email: documentValue,
        dateFrom: dateRangeForAPI.dateFrom,
        dateTo: dateRangeForAPI.dateTo,
      } as OrderGroupPayload;
    }

    if (documentType === 'IDENTITY_DOCUMENT') {
      return {
        tenantId,
        identityDocument: documentValue,
        dateFrom: dateRangeForAPI.dateFrom,
        dateTo: dateRangeForAPI.dateTo,
      } as OrderGroupPayload;
    }

    return null;
  });

  private getOrderGroupsQueryOptions(payload: OrderGroupPayload) {
    return queryOptions({
      queryKey: [
        'orders',
        'groups',
        payload.tenantId,
        payload.identityDocument || payload.email,
        payload.dateFrom,
        payload.dateTo,
      ] as const,
      queryFn: async () => await this.orderService.searchOrderGroups(payload),
    });
  }

  private getOrderDetailQueryOptions(payload: OrderDetailPayload) {
    return queryOptions({
      queryKey: ['orders', 'detail', payload] as const,
      queryFn: async () =>
        await this.orderDetailService.getOrderDetail(payload),
    });
  }

  private getOrderProductsQueryOptions(payload: OrderGroupDetailPayload) {
    return queryOptions({
      queryKey: ['orders', 'products', payload] as const,
      queryFn: async () =>
        await this.orderProductService.getOrderProducts(payload),
    });
  }

  private getOrderTimelineQueryOptions(payload: OrderTimelinePayload) {
    return queryOptions({
      queryKey: ['orders', 'timeline', payload] as const,
      queryFn: async () =>
        await this.orderTimelineService.getOrderTimeline(payload),
    });
  }

  private getInvoicingStatusQueryOptions(payload: OrderInvoicingStatusPayload) {
    return queryOptions({
      queryKey: ['orders', 'invoicing-status', payload] as const,
      queryFn: async () =>
        await this.orderInvoicingStatusService.getInvoicingStatus(payload),
    });
  }

  private getNotificationsQueryOptions(payload: OrderRexAriaPayload) {
    return queryOptions({
      queryKey: ['orders', 'notifications', payload] as const,
      queryFn: async () =>
        await this.orderRexAriaService.fetchNotifications(payload),
    });
  }

  readonly ordersQuery = derivedAsync(() => {
    const payload = this.orderPayload();

    const hasRequiredFields =
      payload &&
      payload.tenantId &&
      (payload.identityDocument || payload.email) &&
      payload.dateFrom &&
      payload.dateTo;

    if (!hasRequiredFields) {
      return undefined;
    }

    return this.query(
      this.getOrderGroupsQueryOptions(payload!)
    ).result$;
  });

  readonly filteredOrders = computed(() => {
    const orders = this.ordersQuery()?.data ?? [];
    const statusFilter = this.statusFilter();
    const orderTypeFilter = this.orderTypeFilter();
    const tenantInfo = this.tenantUrlUtil.tenantUrlInfo();
    
    return OrderService.filterOrders(
      orders, 
      statusFilter, 
      orderTypeFilter,
      this.logger,
      tenantInfo.tenant || undefined
    );
  });

  readonly isLoading = computed(() => this.ordersQuery()?.isLoading ?? false);
  readonly error = computed(() => this.ordersQuery()?.error ?? null);

  readonly hasResults = computed(() => {
    const orders = this.ordersQuery()?.data ?? [];
    return orders.length > 0 && !this.error();
  });

  setStatusFilter(
    status: 'IN_PROGRESS' | 'DELIVERED' | 'CANCELED' | null
  ): void {
    const tenantInfo = this.tenantUrlUtil.tenantUrlInfo();
    const currentOrders = this.ordersQuery()?.data ?? [];

    this.logger.info('Status filter applied', {
      module: 'ORDERS',
      action: 'apply-status-filter',
      tenantId: tenantInfo.tenant,
      status: status || 'ALL',
      orderCount: currentOrders.length
    });

    this.statusFilter.set(status);
  }

  setOrderTypeFilter(type: string | null): void {
    const tenantInfo = this.tenantUrlUtil.tenantUrlInfo();
    const currentOrders = this.ordersQuery()?.data ?? [];

    this.logger.info('Order type filter applied', {
      module: 'ORDERS',
      action: 'apply-type-filter',
      tenantId: tenantInfo.tenant,
      type: type || 'ALL',
      orderCount: currentOrders.length
    });

    this.orderTypeFilter.set(type);
  }

  clear(): void {
    const tenantInfo = this.tenantUrlUtil.tenantUrlInfo();
    const clearedFilters = [];

    if (this.statusFilter()) clearedFilters.push('status');
    if (this.orderTypeFilter()) clearedFilters.push('orderType');

    this.logger.info('Filters cleared', {
      module: 'ORDERS',
      action: 'clear-filters',
      tenantId: tenantInfo.tenant,
      clearedFilters
    });

    this.statusFilter.set(null);
    this.orderTypeFilter.set(null);
  }

  async prefetchOrderGroups(
    tenantId: string,
    identityDocument: string,
    dateFrom: string,
    dateTo: string
  ): Promise<void> {
    this.logger.debug('Prefetching order groups', {
      module: 'ORDERS',
      action: 'prefetch-orders',
      tenantId,
      identityDocument,
      dateFrom,
      dateTo
    });

    const payload: OrderGroupPayload = {
      tenantId,
      identityDocument,
      dateFrom,
      dateTo,
    };

    await this.queryClient.prefetchQuery(
      this.getOrderGroupsQueryOptions(payload)
    );
  }

  async prefetchOrderDetail(payload: OrderDetailPayload): Promise<void> {
    await this.queryClient.prefetchQuery(
      this.getOrderDetailQueryOptions(payload)
    );
  }

  async prefetchOrderProducts(payload: OrderGroupDetailPayload): Promise<void> {
    await this.queryClient.prefetchQuery(
      this.getOrderProductsQueryOptions(payload)
    );
  }

  async prefetchOrderTimeline(payload: OrderTimelinePayload): Promise<void> {
    await this.queryClient.prefetchQuery(
      this.getOrderTimelineQueryOptions(payload)
    );
  }

  async prefetchInvoicingStatus(
    payload: OrderInvoicingStatusPayload
  ): Promise<void> {
    await this.queryClient.prefetchQuery(
      this.getInvoicingStatusQueryOptions(payload)
    );
  }

  async prefetchNotifications(payload: OrderRexAriaPayload): Promise<void> {
    await this.queryClient.prefetchQuery(
      this.getNotificationsQueryOptions(payload)
    );
  }

  invalidateOrderGroups(): void {
    const tenantInfo = this.tenantUrlUtil.tenantUrlInfo();

    this.logger.info('Invalidating order groups queries', {
      module: 'ORDERS',
      action: 'invalidate-orders',
      tenantId: tenantInfo.tenant,
      reason: 'manual-invalidation',
      queryKey: ['orders', 'groups']
    });

    this.queryClient.invalidateQueries({ queryKey: ['orders', 'groups'] });
  }

  invalidateOrderDetail(payload: OrderDetailPayload): void {
    const tenantInfo = this.tenantUrlUtil.tenantUrlInfo();

    this.logger.info('Invalidating order detail queries', {
      module: 'ORDERS',
      action: 'invalidate-orders',
      tenantId: tenantInfo.tenant,
      reason: 'order-detail-update',
      queryKey: ['orders', 'detail', payload]
    });

    this.queryClient.invalidateQueries({
      queryKey: ['orders', 'detail', payload],
    });
  }

  invalidateAll(): void {
    const tenantInfo = this.tenantUrlUtil.tenantUrlInfo();

    this.logger.info('Invalidating all order queries', {
      module: 'ORDERS',
      action: 'invalidate-orders',
      tenantId: tenantInfo.tenant,
      reason: 'global-invalidation',
      queryKey: ['orders']
    });

    this.queryClient.invalidateQueries({ queryKey: ['orders'] });
  }
}
